/**
 * Dashboard Management Thunks
 * 
 * Following the same architecture pattern as authThunks.ts
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles async operations (side effects)
 * - Separation of Concerns: Isolated from state management and data access
 * - Open/Closed: Easy to add new thunks without modifying existing ones
 * - Dependency Inversion: Depends on API abstraction, not implementation
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import {
  IProduct,
  IOrder,
  IProductAnalytics,
  IOrderAnalytics,
  IPrintQueueSummary,
  IProductSearchParams,
  IOrderSearchParams,
  ICreateProductDto,
  IUpdateProductDto,
  IBulkProductOperationDto,
  IProductVariantDto,
  IUpdateProductVariantDto,
} from '../../../types/dashboard.types';

// ============================================
// PRODUCT THUNKS
// ============================================

/**
 * Fetch products with search and pagination
 */
export const fetchProducts = createAsyncThunk(
  'productManagement/fetchProducts',
  async (params: IProductSearchParams = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);
      if (params.category) searchParams.append('category', params.category);
      if (params.vendor) searchParams.append('vendor', params.vendor);
      if (params.stock) searchParams.append('stock', params.stock);
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get(`/admin/products?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch products'));
    }
  }
);

/**
 * Fetch product analytics
 */
export const fetchProductAnalytics = createAsyncThunk(
  'productManagement/fetchProductAnalytics',
  async (params: { period?: string; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.period) searchParams.append('period', params.period);
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const response = await axiosInstance.get(`/admin/products/analytics/overview?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch product analytics'));
    }
  }
);

/**
 * Create new product
 */
export const createProduct = createAsyncThunk(
  'productManagement/createProduct',
  async (productData: ICreateProductDto, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/admin/products', productData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create product'));
    }
  }
);

/**
 * Update product
 */
export const updateProduct = createAsyncThunk(
  'productManagement/updateProduct',
  async ({ id, data }: { id: string; data: IUpdateProductDto }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/products/${id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update product'));
    }
  }
);

/**
 * Archive product (remove from catalog). Never hard-deletes.
 */
export const deleteProduct = createAsyncThunk(
  'productManagement/deleteProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/admin/products/${id}`);
      return response.data as {
        productId: string;
        action: 'archived';
        message: string;
        orderLineCount?: number;
      };
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to archive product in catalog'),
      );
    }
  }
);

/**
 * Bulk archive products (remove from catalog). Never hard-deletes.
 */
export const bulkDeleteProducts = createAsyncThunk(
  'productManagement/bulkDeleteProducts',
  async (bulkData: IBulkProductOperationDto, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/admin/products/bulk/remove-from-catalog',
        bulkData,
      );
      return response.data as {
        archived: number;
        deleted: number;
        failed: number;
        results: Array<{
          productId: string;
          success: boolean;
          action?: 'archived';
        }>;
      };
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to bulk archive products in catalog'),
      );
    }
  }
);

/**
 * Update product stock
 */
export const updateProductStock = createAsyncThunk(
  'productManagement/updateProductStock',
  async ({ id, stockData }: { id: string; stockData: { quantity: number; reason?: string } }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/products/${id}/stock`, stockData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update product stock'));
    }
  }
);

/**
 * Bulk update stock
 */
export const bulkUpdateStock = createAsyncThunk(
  'productManagement/bulkUpdateStock',
  async (stockUpdates: Array<{ productId: string; quantity: number }>, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/admin/products/inventory/bulk-update', stockUpdates);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to bulk update stock'));
    }
  }
);

/**
 * Export products to CSV
 */
export const exportProductsToCsv = createAsyncThunk(
  'productManagement/exportProductsToCsv',
  async (params: IProductSearchParams = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);
      if (params.category) searchParams.append('category', params.category);
      if (params.vendor) searchParams.append('vendor', params.vendor);
      if (params.stock) searchParams.append('stock', params.stock);
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get(`/admin/products/export/csv?${searchParams.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      return 'Export completed';
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export products'));
    }
  }
);

// ============================================
// PRODUCT VARIANT THUNKS
// ============================================

/**
 * Create product variant
 */
export const createProductVariant = createAsyncThunk(
  'productManagement/createProductVariant',
  async ({ productId, variantData }: { productId: string; variantData: IProductVariantDto }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/products/${productId}/variants`, variantData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create product variant'));
    }
  }
);

/**
 * Update product variant
 */
export const updateProductVariant = createAsyncThunk(
  'productManagement/updateProductVariant',
  async ({ variantId, variantData }: { variantId: string; variantData: IUpdateProductVariantDto }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/products/variants/${variantId}`, variantData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update product variant'));
    }
  }
);

/**
 * Delete product variant
 */
export const deleteProductVariant = createAsyncThunk(
  'productManagement/deleteProductVariant',
  async (variantId: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/admin/products/variants/${variantId}`);
      return variantId;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete product variant'));
    }
  }
);

// ============================================
// ORDER THUNKS
// ============================================

/**
 * Fetch orders with search and pagination
 */
export const fetchOrders = createAsyncThunk(
  'orderManagement/fetchOrders',
  async (params: IOrderSearchParams = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);
      if (params.paymentStatus) searchParams.append('paymentStatus', params.paymentStatus);
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get(`/admin/orders?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch orders'));
    }
  }
);

/**
 * Fetch order analytics
 */
export const fetchOrderAnalytics = createAsyncThunk(
  'orderManagement/fetchOrderAnalytics',
  async (params: { period?: string } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.period) searchParams.append('period', params.period);

      const response = await axiosInstance.get(`/admin/orders/analytics?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch order analytics'));
    }
  }
);

/**
 * Fetch print queue summary
 */
export const fetchPrintQueueSummary = createAsyncThunk(
  'orderManagement/fetchPrintQueueSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/admin/ops/print-queue/summary');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch print queue summary'));
    }
  }
);

/**
 * Update order status
 */
export const updateOrderStatus = createAsyncThunk(
  'orderManagement/updateOrderStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/orders/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update order status'));
    }
  }
);

/**
 * Update payment status
 */
export const updatePaymentStatus = createAsyncThunk(
  'orderManagement/updatePaymentStatus',
  async ({ id, paymentStatus }: { id: string; paymentStatus: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/orders/${id}/payment-status`, { paymentStatus });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update payment status'));
    }
  }
);

/**
 * Add tracking number
 */
export const addTrackingNumber = createAsyncThunk(
  'orderManagement/addTrackingNumber',
  async ({ id, trackingNumber, carrier }: { id: string; trackingNumber: string; carrier?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/orders/${id}/tracking`, { trackingNumber, carrier });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to add tracking number'));
    }
  }
);

/**
 * Process refund
 */
export const processRefund = createAsyncThunk(
  'orderManagement/processRefund',
  async ({ id, refundData }: { id: string; refundData: { amount: number; reason: string; notes?: string } }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/orders/${id}/refund`, refundData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to process refund'));
    }
  }
);

/**
 * Export orders to CSV
 */
export const exportOrdersToCsv = createAsyncThunk(
  'orderManagement/exportOrdersToCsv',
  async (params: IOrderSearchParams = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);
      if (params.paymentStatus) searchParams.append('paymentStatus', params.paymentStatus);
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get(`/admin/orders/export?${searchParams.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      return 'Export completed';
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export orders'));
    }
  }
);

/**
 * Print shipping label
 */
export const printShippingLabel = createAsyncThunk(
  'orderManagement/printShippingLabel',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/admin/orders/${id}/shipping-label`, {
        responseType: 'blob',
      });

      // Create download link
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `shipping-label-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      return 'Label printed';
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to print shipping label'));
    }
  }
);

// ============================================
// ADVANCED ANALYTICS THUNKS
// ============================================

/**
 * Fetch advanced order analytics
 */
export const fetchAdvancedOrderAnalytics = createAsyncThunk(
  'orderManagement/fetchAdvancedAnalytics',
  async (params: { dateRange?: { from: string; to: string } } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.dateRange) {
        searchParams.append('from', params.dateRange.from);
        searchParams.append('to', params.dateRange.to);
      }
      
      const response = await axiosInstance.get(`/admin/orders/analytics/advanced?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch advanced analytics'));
    }
  }
);

/**
 * Fetch order communication history
 */
export const fetchOrderCommunications = createAsyncThunk(
  'orderManagement/fetchCommunications',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/admin/orders/${orderId}/communications`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch communications'));
    }
  }
);

/**
 * Send order communication
 */
export const sendOrderCommunication = createAsyncThunk(
  'orderManagement/sendCommunication',
  async (data: {
    orderId: string;
    type: 'EMAIL' | 'SMS' | 'NOTIFICATION';
    templateId?: string;
    customContent?: string;
    subject?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/orders/${data.orderId}/communications`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send communication'));
    }
  }
);

/**
 * Send automated order email
 */
export const sendAutomatedOrderEmail = createAsyncThunk(
  'orderManagement/sendAutomatedEmail',
  async (data: {
    orderId: string;
    emailType: 'ORDER_CONFIRMATION' | 'SHIPPING_NOTIFICATION' | 'DELIVERY_CONFIRMATION' | 'REFUND_NOTIFICATION';
  }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/orders/${data.orderId}/send-email`, {
        emailType: data.emailType,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send automated email'));
    }
  }
);

/**
 * Create communication campaign
 */
export const createCommunicationCampaign = createAsyncThunk(
  'orderManagement/createCampaign',
  async (data: {
    name: string;
    type: 'BULK_EMAIL' | 'ORDER_STATUS_UPDATE' | 'CUSTOMER_SATISFACTION';
    targetOrders: string[];
    templateId: string;
    scheduledAt?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/admin/communications/campaigns', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create campaign'));
    }
  }
);

/**
 * Send customer satisfaction survey
 */
export const sendSatisfactionSurvey = createAsyncThunk(
  'orderManagement/sendSatisfactionSurvey',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/orders/${orderId}/satisfaction-survey`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send satisfaction survey'));
    }
  }
);

/**
 * Get customer satisfaction data
 */
export const fetchCustomerSatisfaction = createAsyncThunk(
  'orderManagement/fetchSatisfaction',
  async (params: { orderId?: string; dateRange?: { from: string; to: string } } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.orderId) searchParams.append('orderId', params.orderId);
      if (params.dateRange) {
        searchParams.append('from', params.dateRange.from);
        searchParams.append('to', params.dateRange.to);
      }
      
      const response = await axiosInstance.get(`/admin/orders/satisfaction?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch satisfaction data'));
    }
  }
);